import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PasswordInput } from '../components/ui/password-input';
import {
  Lock,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  KeyRound,
  Sparkles,
  ShieldCheck,
  Loader2,
} from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AuthLayout from '../components/AuthLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token') || '';
  const [token, setToken] = useState(tokenFromUrl);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Si le token est dans l'URL (?token=...), le pré-remplir et garder synchronisé
  useEffect(() => {
    if (tokenFromUrl) setToken(tokenFromUrl);
  }, [tokenFromUrl]);

  const hasTokenInUrl = Boolean(tokenFromUrl);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/auth/password-reset-confirm`, {
        token,
        new_password: newPassword,
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 400) setError('Le lien de réinitialisation est invalide ou a expiré. Veuillez refaire une demande.');
      else if (status === 404) setError('Lien de réinitialisation non trouvé.');
      else if (!err.response) setError('Impossible de se connecter au serveur. Vérifiez votre connexion internet.');
      else setError(detail || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      variant="default"
      badge="Nouveau mot de passe"
      badgeIcon={KeyRound}
      title="Définir votre"
      titleHighlight="nouveau mot de passe"
      description="Choisissez un mot de passe robuste pour sécuriser durablement l'accès à votre espace."
      features={[
        { icon: ShieldCheck, label: 'Stockage chiffré (bcrypt)' },
        { icon: CheckCircle, label: 'Validation immédiate' },
        { icon: Sparkles, label: 'Connexion automatique après reset' },
      ]}
    >
      <div className="mb-2">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
          data-testid="reset-back-to-login"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à la connexion
        </Link>
      </div>

      <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mt-3 mb-2">
        Réinitialiser le mot de passe
      </h2>
      <p className="text-slate-600 dark:text-slate-300 text-sm mb-7">
        {hasTokenInUrl
          ? 'Choisissez votre nouveau mot de passe ci-dessous. Votre lien sécurisé est déjà rattaché.'
          : 'Collez le token reçu par email puis choisissez votre nouveau mot de passe.'}
      </p>

      {success ? (
        <div className="space-y-5" data-testid="reset-success-state">
          <div className="flex items-start gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl">
            <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
            <div>
              <p className="font-semibold text-emerald-800 dark:text-emerald-200">Mot de passe réinitialisé !</p>
              <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-1">
                Vous allez être redirigé vers la page de connexion…
              </p>
            </div>
          </div>
          <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 animate-pulse w-full" />
          </div>
          <Link to="/login">
            <Button className="w-full h-12 bg-gradient-to-r from-[#0B3C5D] to-[#0a1e3f] hover:from-[#0a3252] hover:to-[#091a36] text-white font-semibold shadow-lg shadow-blue-900/20">
              Aller à la connexion
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span data-testid="reset-error-message">{error}</span>
            </div>
          )}

          {hasTokenInUrl ? (
            <div
              className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-sm text-emerald-800 dark:text-emerald-200 flex items-start gap-2"
              data-testid="reset-token-detected"
            >
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Lien sécurisé détecté</p>
                <p className="text-xs mt-0.5 opacity-80">
                  Votre token de réinitialisation a été lu automatiquement depuis le lien.
                </p>
              </div>
            </div>
          ) : (
            <div>
              <Label htmlFor="reset-token" className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Token de réinitialisation
              </Label>
              <div className="relative mt-1.5">
                <Sparkles className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="reset-token"
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Collez le token reçu par email"
                  className="pl-10 h-12"
                  required
                  data-testid="reset-token-input"
                />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Le token vous a été envoyé par email. Ne pas confondre avec votre PASS FTPE.
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="reset-new" className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              Nouveau mot de passe
            </Label>
            <div className="relative mt-1.5">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
              <PasswordInput
                id="reset-new"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 6 caractères"
                className="pl-10 h-12"
                required
                autoComplete="new-password"
                data-testid="reset-new-password-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="reset-confirm" className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              Confirmer le mot de passe
            </Label>
            <div className="relative mt-1.5">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
              <PasswordInput
                id="reset-confirm"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Retapez votre mot de passe"
                className="pl-10 h-12"
                required
                autoComplete="new-password"
                data-testid="reset-confirm-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 text-base font-semibold bg-gradient-to-r from-[#0B3C5D] to-[#0a1e3f] hover:from-[#0a3252] hover:to-[#091a36] shadow-lg shadow-blue-900/30 hover:shadow-xl group text-white"
            data-testid="reset-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Réinitialisation…
              </>
            ) : (
              <>
                <KeyRound className="w-5 h-5 mr-2" />
                Réinitialiser le mot de passe
              </>
            )}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
};

export default ResetPassword;
